import { createSlice, createAction } from '@reduxjs/toolkit';
import { IState } from 'shared/interfaces/state.interface';
import { ITag } from 'shared/interfaces/tag.interface';

export const getAllTags = createAction('tags/getAllTags');

export const createTag = createAction(
  'tags/createTag',
  (payload: {
    name: string;
    color: string;
    description?: string;
    ownerModel: 'affiliate' | 'user';
    orderId?: string;
    targetModel?: string;
  }) => ({
    payload
  })
);

export const updateTag = createAction(
  'tags/updateTag',
  (payload: {
    id: string;
    data: Partial<{ name: string; color: string; description?: string }>;
  }) => ({
    payload
  })
);

export const deleteTag = createAction('tags/deleteTag', (payload: string) => ({
  payload
}));

export const associateTagWithOrder = createAction(
  'tags/associateTagWithOrder',
  (payload: { tagId: string; orderId: string }) => ({ payload })
);

export const disassociateTagFromOrder = createAction(
  'tags/disassociateTagFromOrder',
  (payload: { tagId: string; orderId: string }) => ({ payload })
);

export const associateTagWithOrderSuccess = createAction(
  'tags/associateTagWithOrderSuccess'
);

export const associateTagWithOrderFail = createAction(
  'tags/associateTagWithOrderFail',
  (payload: string) => ({ payload })
);

export const disassociateTagFromOrderSuccess = createAction(
  'tags/disassociateTagFromOrderSuccess'
);

export const disassociateTagFromOrderFail = createAction(
  'tags/disassociateTagFromOrderFail',
  (payload: string) => ({ payload })
);

interface TagsState {
  allTags: ITag[];
  isLoading: boolean;
  error: string | null;
}

const initialState: TagsState = {
  allTags: [],
  isLoading: false,
  error: null
};

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    getTagsData: (state) => {
      if (!state.isLoading) {
        state.isLoading = true;
        state.error = null;
      }
    },
    setTagsData: (state, { payload }) => {
      state.isLoading = false;
      state.allTags = payload;
      state.error = null;
    },
    getTagsFail: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload || 'Failed to fetch tags';
    },
    addTag: (state, { payload }) => {
      state.allTags.push(payload);
      state.isLoading = false;
      state.error = null;
    },
    updateTagInState: (state, { payload }) => {
      const index = state.allTags.findIndex((tag) => tag.id === payload.id);
      if (index !== -1) {
        state.allTags[index] = payload;
      }
      state.isLoading = false;
      state.error = null;
    },
    removeTag: (state, { payload }) => {
      state.allTags = state.allTags.filter((tag) => tag.id !== payload);
      state.isLoading = false;
      state.error = null;
    },
    setLoading: (state, { payload }) => {
      state.isLoading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      state.isLoading = false;
    }
  }
});

export const {
  getTagsData,
  setTagsData,
  getTagsFail,
  addTag,
  updateTagInState,
  removeTag,
  setLoading,
  setError
} = tagsSlice.actions;

export const { reducer } = tagsSlice;

export const getAllTagsSelector = (state: IState) =>
  (state && state.tags?.allTags) || [];

export const getTagsLoadingSelector = (state: IState) =>
  (state && state.tags?.isLoading) || false;

export const getTagsErrorSelector = (state: IState) =>
  (state && state.tags?.error) || null;
